// Context list (CONTRACT "Context list"): 256 px on the wall step, drag 240
// to 320 (persisted), "[" collapses. Head 44 carries the active rail group's
// name; under it sit that group's destination rows and then the destination's
// own context, which the view supplies through useListContext (shell.html:
// "A page keeps only its group's rows in the top zone and puts the
// destination context below"). A destination that supplies nothing gets the
// shell's own context: Sources as text rows with presence dots, and the First
// value checklist. Foot 44 holds the avatar, name, role and the density
// segmented; the account menu itself lives in the top bar.
//
// The under-1024 menu reuses this file's rows, filtered by authenticated role,
// because there the rail and the list are both gone (contract 2.2: every
// permitted destination stays directly reachable).
import { useCallback, useRef, type KeyboardEvent, type PointerEvent } from 'react';
import { SidebarSimple } from '@phosphor-icons/react';
import type { AuthIdentity, WorkspaceRole } from '../api/types';
import { useView, type ViewId } from '../router/useView';
import { Button } from '../ui/Button';
import { PresenceDot } from '../ui/PresenceDot';
import { Segmented } from '../ui/Segmented';
import type { DensityMode } from '../theme/density';
import { groupOfForRole, railGroupsForRole, type RailGroup } from './destinations';
import { FirstValueChecklist, type FirstValueInputs } from './FirstValueChecklist';
import { useShell } from './ShellContext';
import { LIST_WIDTH } from './shellPrefs';
import type { SourceRoute } from './sourceRoutes';

// The list foot offers the two measured modes (CONTRACT "Density").
const DENSITY_OPTIONS: ReadonlyArray<{ readonly value: DensityMode; readonly label: string }> = [
  { value: 'default', label: 'Default' },
  { value: 'compact', label: 'Compact' },
];
const RESIZE_STEP = 8;

export interface ContextListProps {
  readonly identity: AuthIdentity | null;
  readonly accessRole?: WorkspaceRole | null;
  readonly routes: ReadonlyArray<SourceRoute>;
  readonly routesState: 'ready' | 'loading' | 'error';
  readonly firstValue: FirstValueInputs;
  readonly density: DensityMode;
  readonly onDensity: (mode: DensityMode) => void;
  readonly width: number;
}

// Source-family ticks (D6, owner-approved 2026-08-27: "i like this idea of
// seperating telegram diord and x by color"): the three source destinations
// carry a 3 px tick in their family colour, dots and glyphs only per the
// token contract, never fills or stripes on the row itself.
const SOURCE_TICKS: Partial<Record<ViewId, string>> = {
  telegram: 'var(--color-src-telegram)',
  discord: 'var(--color-src-discord)',
  x: 'var(--color-src-x)',
};

// showHeaders is false in the context list, where the 44 px head above these
// rows already names the group (CONTRACT "Context list": "list head 44 (group
// name, collapse)"). Printing the same word again as a group header stacked it
// under the rail tile and the head, so "Settings" appeared four times inside
// 250 x 150 px. The under-1024 menu lists all three groups, where the headers
// are the only thing telling them apart, so it passes true.
export function DestinationGroups({
  groups,
  onNavigate,
  showHeaders = true,
}: {
  readonly groups: ReadonlyArray<RailGroup>;
  readonly onNavigate?: (() => void) | undefined;
  readonly showHeaders?: boolean;
}) {
  const { view, navigate } = useView();
  const go = (id: ViewId) => {
    navigate(id);
    onNavigate?.();
  };
  return (
    <>
      {groups.map((group) => (
        <div className="grp" key={group.id}>
          {showHeaders ? (
            <div className="grp-h">
              <span>{group.label}</span>
            </div>
          ) : null}
          {group.destinations.map((destination) => (
            <a
              key={destination.id}
              className="lrow"
              href={`?view=${destination.id}`}
              aria-current={destination.id === view ? 'page' : undefined}
              onClick={(event) => {
                event.preventDefault();
                go(destination.id);
              }}
            >
              {SOURCE_TICKS[destination.id] ? (
                <span
                  className="lrow-tick"
                  style={{ background: SOURCE_TICKS[destination.id] }}
                  aria-hidden="true"
                />
              ) : null}
              {destination.label}
            </a>
          ))}
        </div>
      ))}
    </>
  );
}

export function SourcesBlock({
  routes,
  routesState,
}: Pick<ContextListProps, 'routes' | 'routesState'>) {
  const { navigate } = useView();
  const active = routes.filter(
    (route) => route.presence === 'live' || route.presence === 'stale',
  ).length;
  return (
    <div className="grp">
      <div className="grp-h">
        <span>Sources</span>
        <span className="grp-n num">
          {routesState === 'loading'
            ? 'checking'
            : routesState === 'error'
              ? 'unavailable'
              : `${active} of ${routes.length} routes`}
        </span>
      </div>
      {routes.map((route) => (
        <button
          key={route.kind}
          type="button"
          className="src-row"
          title={`${route.label}. ${route.reason}`}
          onClick={() => navigate('sources')}
        >
          <PresenceDot presence={route.presence} />
          <span className="src-nm">{route.label}</span>
          <span className="src-meta">{route.meta}</span>
        </button>
      ))}
    </div>
  );
}

// The under-1024 sheet: every destination, because neither rail nor list is
// on screen at that width.
export function MenuDestinations({
  onNavigate,
  role = null,
}: {
  readonly onNavigate?: (() => void) | undefined;
  readonly role?: WorkspaceRole | null;
}) {
  return (
    <div className="list-scroll">
      <DestinationGroups groups={railGroupsForRole(role)} onNavigate={onNavigate} />
    </div>
  );
}

function ListFoot({
  identity,
  density,
  onDensity,
}: Pick<ContextListProps, 'identity' | 'density' | 'onDensity'>) {
  const name = identity?.user.displayName ?? 'Signed in';
  const role = identity?.role ?? 'role unavailable';
  return (
    <div className="list-foot">
      <span className="avatar" aria-hidden="true">
        {name.trim().charAt(0).toLowerCase() || 'a'}
      </span>
      <span className="list-who">
        <span className="list-who-nm">{name}</span>
        <span className="list-who-role">{role}</span>
      </span>
      <Segmented
        label="Density"
        size="sm"
        value={density}
        onChange={onDensity}
        options={DENSITY_OPTIONS}
      />
    </div>
  );
}

export function ContextList(props: ContextListProps) {
  const { identity, accessRole, routes, routesState, firstValue, density, onDensity, width } =
    props;
  const { toggleList, setListWidth, listContext } = useShell();
  const { view } = useView();
  const group = groupOfForRole(view, accessRole ?? identity?.role ?? null);
  const dragStart = useRef<{ x: number; width: number } | null>(null);

  const onHandleDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      dragStart.current = { x: event.clientX, width };
    },
    [width],
  );
  const onHandleMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (!dragStart.current) return;
      setListWidth(dragStart.current.width + event.clientX - dragStart.current.x, false);
    },
    [setListWidth],
  );
  const onHandleUp = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (!dragStart.current) return;
      setListWidth(dragStart.current.width + event.clientX - dragStart.current.x, true);
      dragStart.current = null;
    },
    [setListWidth],
  );
  const onHandleKey = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setListWidth(width - RESIZE_STEP, true);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        setListWidth(width + RESIZE_STEP, true);
      }
      if (event.key === 'Home') {
        event.preventDefault();
        setListWidth(LIST_WIDTH.min, true);
      }
      if (event.key === 'End') {
        event.preventDefault();
        setListWidth(LIST_WIDTH.max, true);
      }
    },
    [setListWidth, width],
  );

  return (
    <aside className="list" aria-label="Context list" data-region="list" tabIndex={-1}>
      <div className="list-head">
        <span>{group.label}</span>
        <Button
          variant="icon"
          size="sm"
          aria-label="Collapse list, key ["
          icon={<SidebarSimple />}
          onClick={toggleList}
        />
      </div>
      <div className="list-scroll">
        {/* A group with one destination that carries the group's own name
            (Alerts, Settings) is already named by the head above and by the
            rail tile beside it, and the row could only ever be the page you
            are on, so it says the word a third time and opens nothing new. */}
        {group.destinations.length > 1 ? (
          <DestinationGroups groups={[group]} showHeaders={false} />
        ) : null}
        {listContext ? (
          listContext.body
        ) : (
          <>
            <SourcesBlock routes={routes} routesState={routesState} />
            <FirstValueChecklist input={firstValue} />
          </>
        )}
      </div>
      <ListFoot identity={identity} density={density} onDensity={onDensity} />
      <div
        className="list-handle"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize context list"
        aria-valuemin={LIST_WIDTH.min}
        aria-valuemax={LIST_WIDTH.max}
        aria-valuenow={width}
        tabIndex={0}
        onPointerDown={onHandleDown}
        onPointerMove={onHandleMove}
        onPointerUp={onHandleUp}
        onKeyDown={onHandleKey}
      />
    </aside>
  );
}
